/**
 * SVG Preview Component
 * Displays generated SVG with zoom and pan controls
 */

import { useState, useEffect, useCallback, useRef } from 'react';

interface SvgPreviewProps {
  svgContent?: string;
  pageCount?: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

export function SvgPreview({
  svgContent,
  pageCount = 1,
  currentPage,
  onPageChange,
  isLoading
}: SvgPreviewProps) {
  const [zoom, setZoom] = useState(100);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + 25, 500));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev - 25, 25));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoom(100);
  }, []);

  // Reset zoom when content changes
  useEffect(() => {
    setZoom(100);
  }, [svgContent]);

  // Handle mouse wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -25 : 25;
      setZoom(prev => Math.max(25, Math.min(500, prev + delta)));
    }
  }, []);

  return (
    <div className="preview-panel panel">
      <div className="panel-header" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontWeight: 'normal', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <button
            onClick={handleZoomOut}
            disabled={zoom <= 25}
            style={{ padding: '0.125rem 0.5rem' }}
          >
            -
          </button>
          <button
            onClick={handleZoomReset}
            style={{ padding: '0.125rem 0.5rem' }}
          >
            {zoom}%
          </button>
          <button
            onClick={handleZoomIn}
            disabled={zoom >= 500}
            style={{ padding: '0.125rem 0.5rem' }}
          >
            +
          </button>
        </span>
        <span style={{ fontSize: '12px', color: '#6c757d' }}>
          Ctrl+Scroll to zoom
        </span>
      </div>

      <div
        className="preview-container"
        ref={containerRef}
        onWheel={handleWheel}
      >
        {isLoading ? (
          <div className="loading">Generating preview...</div>
        ) : svgContent ? (
          <div className="preview-scroll-area">
            <div
              className="preview-svg"
              style={{
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top left',
                width: `${zoom}%`,
                height: `${zoom}%`
              }}
              dangerouslySetInnerHTML={{ __html: svgContent }}
            />
          </div>
        ) : (
          <div className="loading">Configure drawing to see preview</div>
        )}
      </div>

      {pageCount > 1 && (
        <div className="page-controls">
          <button
            className="btn btn-secondary"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 0}
          >
            Previous
          </button>
          <span>
            Page {currentPage + 1} of {pageCount}
          </span>
          <button
            className="btn btn-secondary"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= pageCount - 1}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
